import { $http } from "@/plugins/api";
import { Commit } from "vuex";
import {
  IncidentSummary,
  IncidentDetails,
  AffectedUser,
  ReceivedHeader,
  RecipientDetails,
} from "../types/IncidentSummary";

interface IncidentSummaryState {
  incidentSummary: IncidentSummary | null;
  incidentDetails: IncidentDetails | null;
  affectedUsers: AffectedUser[];
  receivedHeaders: ReceivedHeader[];
  recipientsDetails: RecipientDetails[];
}
type IncidentActionPayload = {
  globalOrganisationID: string;
  internetMessageID: string;
  mailSpMessageID: string;
};

type UserActionPayload = {
  globalOrganisationID: string;
  internetMessageID: string;
  customerAccountID: string;
};

type HeaderActionPayload = {
  customerGlobalAccountID: string;
  mailbox: string;
  mailSpMessageID: string;
};

type StatusActionPayload = {
  globalOrganisationID: string;
  internetMessageID: string;
  mailSpMessageID: string;
  status: string;
};

const state: IncidentSummaryState = {
  incidentSummary: null,
  incidentDetails: null,
  affectedUsers: [],
  receivedHeaders: [],
  recipientsDetails: [],
};

const getters = {
  incidentSummary: (state: IncidentSummaryState) => state.incidentSummary,
  incidentDetails: (state: IncidentSummaryState) => state.incidentDetails,
  affectedUsers: (state: IncidentSummaryState) => state.affectedUsers,
  receivedHeaders: (state: IncidentSummaryState) => state.receivedHeaders,
  recipientsDetails: (state: IncidentSummaryState) => state.recipientsDetails,
};

const actions = {
  async getIncidentSummary(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
      mailSpMessageID,
    }: IncidentActionPayload,
  ) {
    commit("setIncidentSummary", null);
    const encodedMailSpMessageID = encodeURIComponent(mailSpMessageID);
    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/history/${globalOrganisationID}/incidents/${internetMessageID}/${encodedMailSpMessageID}`,
      {
        _hasCustomErrorHandling: true,
      },
    );

    commit("setIncidentSummary", res.data);
  },
  async getIncidentDetails(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
      mailSpMessageID,
    }: IncidentActionPayload,
  ) {
    commit("setIncidentDetails", null);
    const encodedMailSpMessageID = encodeURIComponent(mailSpMessageID);

    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/history/${globalOrganisationID}/incidents/${internetMessageID}/${encodedMailSpMessageID}/details`,
      {
        _hasCustomErrorHandling: true,
      },
    );

    commit("setIncidentDetails", res.data);
  },
  async getAffectedUsers(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
      customerAccountID,
    }: UserActionPayload,
  ) {
    commit("setAffectedUsers", []);

    let url = "";

    if (customerAccountID) {
      url = `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/msg/${customerAccountID}/incidents/${internetMessageID}/affected-users`;
    } else {
      url = `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/msg/${globalOrganisationID}/incidents/${internetMessageID}/affected-users`;
    }

    const res = await $http.get(url);

    commit("setAffectedUsers", res.data);
  },
  async getRecipientsDetails(
    { commit }: { commit: Commit },
    {
      globalOrganisationID,
      internetMessageID,
    }: { globalOrganisationID: string; internetMessageID: string },
  ) {
    commit("setRecipientsDetails", []);

    const res = await $http.get(
      `${import.meta.env.VITE_API_CLIENT_V3}/history/recipients/${globalOrganisationID}/${internetMessageID}`,
    );

    commit("setRecipientsDetails", res.data);
  },
  async getReceivedHeaders(
    { commit }: { commit: Commit },
    { customerGlobalAccountID, mailbox, mailSpMessageID }: HeaderActionPayload,
  ) {
    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/mail-message/${customerGlobalAccountID}/${mailbox}/${mailSpMessageID}/headers`,
    );

    commit("setReceivedHeaders", res.data);
  },
  async updateIncidentStatus(
    { commit, state }: { commit: Commit; state: IncidentSummaryState },
    {
      globalOrganisationID,
      internetMessageID,
      mailSpMessageID,
      status,
    }: StatusActionPayload,
  ) {
    status = status === "AdminRemediated_NonPhish" ? "safe" : "junk";

    const res = await $http.put(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v1/pt/msg/${globalOrganisationID}/incidents/${internetMessageID}/${mailSpMessageID}/status`,
      {
        status,
      },
    );

    commit("setIncidentSummary", {
      ...state.incidentSummary,
      status:
        res.data.status === "junk"
          ? "AdminRemediated_Phish"
          : "AdminRemediated_NonPhish",
    });
    return res.data.data;
  },
};

const mutations = {
  setIncidentDetails: (state: IncidentSummaryState, data: IncidentDetails) =>
    (state.incidentDetails = data),
  setIncidentSummary: (state: IncidentSummaryState, data: IncidentSummary) =>
    (state.incidentSummary = data),
  setAffectedUsers: (state: IncidentSummaryState, data: AffectedUser[]) =>
    (state.affectedUsers = data),
  setRecipientsDetails: (
    state: IncidentSummaryState,
    data: RecipientDetails[],
  ) => (state.recipientsDetails = data),
  setReceivedHeaders: (state: IncidentSummaryState, data: ReceivedHeader[]) =>
    (state.receivedHeaders = data),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
